import { ABSENT_ENFORCEMENT_MODE, iamLog } from '@identity/application/observability';
import type { AccountStatusViewDto } from '@identity/application/dtos';
import { mapAccountStatusToViewDto } from '@identity/application/mappers';
import type { GetAccountStatusOptions, IAuthApiPort, ICredentialSourcePort } from '@identity/application/ports';
import { extractErrorAttributes, pendingRequirementsTag, subjectAttributes } from '@identity/application/utils';
import type { IdentityFlow } from '@identity/domain/types';

/**
 * Reads the account status. It is the only place that reads it.
 *
 * The enforcement mode and the pending requirements are tagged on the successful read, which is what
 * makes an event for the blocking dialog unnecessary: those two tags plus the flow already describe
 * the population that sees it, and they are emitted where it is known that the backend answered.
 *
 * The mode is reported as it arrived and only its absence becomes a value, so a string this build
 * does not know reaches the dashboard instead of being flattened into `off`.
 *
 * It forwards the verification the caller named and does not interpret it: what that changes about
 * the answer is the backend's business.
 */
export const getAccountStatusUseCase = (
  authApi: IAuthApiPort,
  credentials: ICredentialSourcePort,
  flow: IdentityFlow,
) => {
  const execute = async (options?: GetAccountStatusOptions): Promise<AccountStatusViewDto> => {
    const subject = { ...subjectAttributes(credentials.readUserId()), 'iam.flow': flow };

    try {
      const status = mapAccountStatusToViewDto(await authApi.getAccountStatus(options));

      iamLog.info('accountStatusLoaded', {
        ...subject,
        'iam.enforcementMode': status.enforcementMode ?? ABSENT_ENFORCEMENT_MODE,
        'iam.pendingRequirements': pendingRequirementsTag(status.requirements),
      });

      return status;
    } catch (error) {
      iamLog.error('accountStatusLoaded', { ...subject, ...extractErrorAttributes(error) });
      throw error;
    }
  };

  return { execute };
};
